/* ArmorBatch Global — standalone printable A4 sales flyer.
   Public route /flyer. Dark industrial brochure, print/PDF ready (A4, exact colours).
   All brand colours are inline / in a scoped <style> so they survive print. */
import {
  Shield, Printer, MapPin, MonitorSmartphone, FileText, Users, BarChart3, ShieldCheck,
  AlertTriangle, Cloud, UserPlus, Radio, Activity, Building2, Truck, HardHat, Check,
  Globe, Mail, Phone, ArrowRight,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";

const INK = "#0E0F11";
const PANEL = "#17191C";
const PANEL2 = "#1E2125";
const LINE = "#2B2F35";
const BLUE = "#1E88E5";
const MUTED = "#9BA1A8";
const DEMO_URL = "https://armorbatch.com/login";

const FEATURES = [
  { icon: MapPin, title: "Live GPS Truck Tracking", desc: "See every truck move in real time with precise ETAs." },
  { icon: MonitorSmartphone, title: "War Room Dispatch Screen", desc: "One live board to run the whole day's deliveries." },
  { icon: FileText, title: "Digital Job Dockets", desc: "Paperless dockets, signed on-site and shared instantly." },
  { icon: Users, title: "Contractor & Supplier Portal", desc: "Give partners their own live view — no logins to chase." },
  { icon: BarChart3, title: "Real-Time Reporting & Analytics", desc: "Volumes, turnaround and performance at a glance." },
  { icon: ShieldCheck, title: "Multi-User Role Access", desc: "Dispatchers, drivers, managers — the right view for each." },
];
const PROBLEMS = [
  { t: "Still running paper dockets?", d: "Lost tickets, double-entry and end-of-day chaos." },
  { t: "Losing track of trucks?", d: "Phone calls to find out where loads actually are." },
  { t: "Missing deliveries?", d: "No live ETAs means angry customers and idle crews." },
];
const AUDIENCE = [
  { icon: Building2, t: "Concrete Batching Plants" },
  { icon: Truck, t: "Fleet & Delivery Businesses" },
  { icon: HardHat, t: "Pump Companies & Contractors" },
];
const SAAS = ["Cloud-based — nothing to install", "No hardware required", "Access from any device",
  "14-day free trial", "Simple monthly subscription"];
const STEPS = [
  { n: "1", t: "Invite Your Team", d: "Add dispatchers, drivers and managers in minutes." },
  { n: "2", t: "Dispatch Jobs Live", d: "Send loads to trucks straight from the War Room." },
  { n: "3", t: "Track Everything", d: "Watch every delivery unfold in real time." },
];
const INTEGRATIONS = ["DornerBatch", "Command Alkon", "Sysdyne", "Rinstrum", "and more"];

const Divider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 7px" }}>
    <span style={{ width: 20, height: 3, background: BLUE }} />
    <span style={{ fontFamily: "'Oswald','Arial Narrow',sans-serif", fontWeight: 700, letterSpacing: "0.18em",
      textTransform: "uppercase", fontSize: 12.5, color: "#fff" }}>{label}</span>
    <span style={{ flex: 1, height: 1, background: LINE }} />
  </div>
);

export default function Flyer() {
  return (
    <div className="fly-root">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print { .fly-noprint { display: none !important; } html, body { background: ${INK} !important; } .fly-root { padding: 0 !important; } .fly-page { box-shadow: none !important; height: 297mm !important; min-height: 0 !important; overflow: hidden !important; page-break-after: avoid; } }
        .fly-root { background: #0a0a0b; min-height: 100vh; padding: 24px 12px; display: flex; flex-direction: column; align-items: center;
          font-family: 'Inter', system-ui, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .fly-page { width: 210mm; min-height: 297mm; background: ${INK}; color: #E7E9EC; position: relative; overflow: hidden;
          box-shadow: 0 30px 90px rgba(0,0,0,.6); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .fly-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .fly-grain { position: absolute; inset: 0; opacity: .5; pointer-events: none; background-image:
          radial-gradient(${BLUE}14 1px, transparent 1px); background-size: 22px 22px; }
        .fly-card { background: ${PANEL}; border: 1px solid ${LINE}; }
        .fly-head { font-family: 'Oswald','Arial Narrow',sans-serif; text-transform: uppercase; }
      `}</style>

      <div className="fly-noprint" style={{ marginBottom: 16, display: "flex", gap: 10 }}>
        <button data-testid="flyer-print-btn" onClick={() => window.print()}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: BLUE, color: "#1a1206",
            fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", fontSize: 13, padding: "12px 22px",
            border: "none", cursor: "pointer", fontFamily: "'Oswald',sans-serif" }}>
          <Printer size={16} /> Print / Save as PDF
        </button>
        <Link to="/explore" data-testid="flyer-explore-btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: "#E7E9EC",
            fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", fontSize: 13, padding: "12px 22px",
            border: `1px solid ${LINE}`, cursor: "pointer", fontFamily: "'Oswald',sans-serif", textDecoration: "none" }}>
          See more <ArrowRight size={16} />
        </Link>
      </div>

      <div className="fly-page" data-testid="flyer-page">
        <div className="fly-grain" />
        {/* orange spine */}
        <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 7, background: BLUE }} />

        <div style={{ position: "relative", padding: "14px 30px 14px 34px" }}>
          {/* HEADER */}
          <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Shield size={34} color={BLUE} strokeWidth={2.5} />
                <div style={{ lineHeight: 1 }}>
                  <div className="fly-head" style={{ fontWeight: 800, fontSize: 30, color: "#fff", letterSpacing: ".01em" }}>
                    ARMOR<span style={{ color: BLUE }}>BATCH</span>
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 9.5, letterSpacing: ".4em", color: MUTED, textTransform: "uppercase" }}>Global</div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right", maxWidth: 260 }}>
              <div style={{ color: MUTED, fontSize: 9, letterSpacing: ".25em", textTransform: "uppercase" }}>Smart Dispatch Platform</div>
            </div>
          </header>

          <h1 className="fly-head" style={{ marginTop: 9, fontSize: 23, lineHeight: 1.08, fontWeight: 700, color: "#fff", maxWidth: 560 }}>
            The Smart Dispatch Platform for <span style={{ color: BLUE }}>Concrete & Fleet</span> Businesses
          </h1>
          <div style={{ height: 1, background: LINE, margin: "9px 0 11px" }} />

          {/* PROBLEM */}
          <Divider label="The Problem" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 10 }}>
            {PROBLEMS.map((p) => (
              <div key={p.t} className="fly-card" style={{ padding: "9px 12px", borderLeft: `3px solid ${BLUE}` }}>
                <AlertTriangle size={17} color={BLUE} />
                <div className="fly-head" style={{ fontWeight: 700, fontSize: 12.5, color: "#fff", marginTop: 7, lineHeight: 1.15 }}>{p.t}</div>
                <div style={{ fontSize: 10, color: MUTED, marginTop: 4, lineHeight: 1.35 }}>{p.d}</div>
              </div>
            ))}
          </div>

          {/* SOLUTION */}
          <Divider label="The Solution" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 10 }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="fly-card" style={{ padding: "9px 12px 10px" }}>
                <div style={{ width: 32, height: 32, background: `${BLUE}1F`, border: `1px solid ${BLUE}55`,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <f.icon size={17} color={BLUE} />
                </div>
                <div className="fly-head" style={{ fontWeight: 700, fontSize: 12, color: "#fff", marginTop: 7, lineHeight: 1.15 }}>{f.title}</div>
                <div style={{ fontSize: 9.5, color: MUTED, marginTop: 4, lineHeight: 1.35 }}>{f.desc}</div>
              </div>
            ))}
          </div>

          {/* INTEGRATIONS */}
          <div style={{ background: PANEL2, border: `1px solid ${LINE}`, padding: "9px 16px", marginBottom: 9, position: "relative" }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: 4, bottom: 0, background: BLUE }} />
            <div className="fly-head" style={{ fontWeight: 700, fontSize: 13, color: "#fff", letterSpacing: ".06em" }}>Works With Your Existing Systems</div>
            <div style={{ fontSize: 10, color: MUTED, marginTop: 4, lineHeight: 1.4, maxWidth: 620 }}>
              ArmorBatch sits on top of your current batching or fleet software — no ripping out what already works.
              Your data, your systems, now with a live dispatch layer on top.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 8 }}>
              {INTEGRATIONS.map((i) => (
                <span key={i} style={{ fontFamily: "'Oswald',sans-serif", fontSize: 10.5, letterSpacing: ".05em", textTransform: "uppercase",
                  color: i === "and more" ? MUTED : "#fff", border: `1px solid ${i === "and more" ? LINE : BLUE + "88"}`,
                  padding: "4px 11px", background: i === "and more" ? "transparent" : `${BLUE}12` }}>{i}</span>
              ))}
            </div>
          </div>

          {/* WHO IT'S FOR + HOW IT WORKS (two columns) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 9 }}>
            <div>
              <Divider label="Who It's For" />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {AUDIENCE.map((a) => (
                  <div key={a.t} className="fly-card" style={{ display: "flex", alignItems: "center", gap: 11, padding: "7px 12px" }}>
                    <a.icon size={18} color={BLUE} />
                    <span className="fly-head" style={{ fontWeight: 600, fontSize: 12, color: "#fff" }}>{a.t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Divider label="How It Works" />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {STEPS.map((s) => (
                  <div key={s.n} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                    <div className="fly-head" style={{ flex: "0 0 auto", width: 26, height: 26, background: BLUE, color: INK,
                      fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.n}</div>
                    <div>
                      <div className="fly-head" style={{ fontWeight: 700, fontSize: 12, color: "#fff" }}>{s.t}</div>
                      <div style={{ fontSize: 9.5, color: MUTED, lineHeight: 1.35 }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SAAS band */}
          <div style={{ background: PANEL, border: `1px solid ${LINE}`, padding: "8px 16px", marginBottom: 9 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
              <Cloud size={17} color={BLUE} />
              <span className="fly-head" style={{ fontWeight: 700, fontSize: 13, color: "#fff", letterSpacing: ".06em" }}>A True Cloud SaaS Platform</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
              {SAAS.map((s) => (
                <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, color: "#D7DADE" }}>
                  <Check size={13} color={BLUE} strokeWidth={3} /> {s}
                </span>
              ))}
            </div>
          </div>

          {/* FOUNDER'S NOTE */}
          <div style={{ background: PANEL2, border: `1px solid ${LINE}`, borderLeft: `4px solid ${BLUE}`, padding: "9px 18px 9px", marginBottom: 9, position: "relative" }}>
            <div className="fly-head" style={{ position: "absolute", top: 2, right: 14, fontSize: 48, lineHeight: 1, color: `${BLUE}2E`, fontWeight: 800 }}>”</div>
            <div className="fly-head" style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: ".16em", color: BLUE, textTransform: "uppercase", marginBottom: 5 }}>Built by an operator, for operators</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.3, color: "#fff", fontWeight: 600, maxWidth: 640, fontStyle: "italic" }}>
              "I'm not a developer who studied concrete. I'm a concrete operator who built the software the industry actually needs."
            </div>
            <div style={{ fontSize: 9.5, color: MUTED, marginTop: 5, lineHeight: 1.35, maxWidth: 660 }}>
              30 years in the yard — trucks sitting idle, pump crews chasing ETAs, dispatch run off a whiteboard. So I built ArmorBatch:
              live GPS dispatch, AI truck prediction and partner portals that plug straight into Command Alkon, Sysdyne &amp; Dorner. No rip-and-replace. Live in hours.
            </div>
            <div className="fly-head" style={{ fontSize: 10, color: "#fff", fontWeight: 700, letterSpacing: ".06em", marginTop: 6 }}>
              — Founder &amp; CEO, <span style={{ color: BLUE }}>ArmorBatch Global</span>
            </div>
          </div>

          {/* CTA */}
          <div style={{ background: BLUE, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
            <div>
              <div className="fly-head" style={{ fontWeight: 800, fontSize: 20, color: "#fff", lineHeight: 1 }}>Book a Demo</div>
              <div style={{ fontSize: 10.5, color: "#dbeafe", marginTop: 4, fontWeight: 600 }}>See ArmorBatch run your yard live — start your 14-day free trial.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", marginTop: 9 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, color: "#fff", fontWeight: 600 }}><Globe size={12} color={INK} /> armorbatch.com</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, color: "#fff", fontWeight: 600 }}><Mail size={12} color={INK} /> admin@armorbatch.com</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, color: "#fff", fontWeight: 600 }}><Phone size={12} color={INK} /> 0400 597 850</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div data-testid="flyer-qr" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                background: "#fff", padding: "8px 8px 6px", border: `1px solid ${INK}` }}>
                <QRCodeSVG value={DEMO_URL} size={58} level="M" bgColor="#ffffff" fgColor={INK} />
                <span className="fly-head" style={{ fontSize: 7.5, letterSpacing: ".12em", color: INK, fontWeight: 700 }}>SCAN FOR DEMO</span>
              </div>
              <div className="fly-head" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: INK, color: "#fff",
                fontWeight: 700, fontSize: 13, letterSpacing: ".05em", padding: "11px 18px", whiteSpace: "nowrap" }}>
                armorbatch.com <ArrowRight size={15} color={BLUE} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
